<template>
  <section
    class="runtime-config-overlay"
    :style="themeStyle"
    role="status"
    aria-live="assertive"
    aria-busy="true"
  >
    <div class="runtime-config-overlay__panel">
      <div class="runtime-config-overlay__logo">
        <img src="../assets/logo.png" alt="Suite241" />
      </div>
      <h1>Suite241</h1>
      <p v-if="!hasError">Chargement de la configuration…</p>
      <p v-else class="runtime-config-overlay__error">{{ errorMessage }}</p>
      <div v-if="!hasError" class="runtime-config-overlay__gauge" aria-hidden="true">
        <span></span>
      </div>
      <button v-else type="button" class="runtime-config-overlay__retry" @click="retry">
        <i class="fa fa-rotate-right" aria-hidden="true"></i>
        Réessayer
      </button>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { adminThemeStyle, getAdminTheme } from '@/support/adminThemes';

export default defineComponent({
  name: 'AppRuntimeConfigOverlay',
  computed: {
    userSettings(): Record<string, unknown> {
      return this.$store.getters['userSettings/settings'] || {};
    },
    themeStyle(): Record<string, string> {
      const appearance = this.userSettings.appearance as Record<string, unknown> | undefined;

      return adminThemeStyle(getAdminTheme(appearance?.admin_theme).key);
    },
    hasError(): boolean {
      return Boolean(this.$store.getters['runtime/loadError']);
    },
    errorMessage(): string {
      return this.$store.getters['runtime/loadError'] || '';
    },
  },
  methods: {
    async retry(): Promise<void> {
      try {
        await this.$store.dispatch('runtime/load', { force: true });
        await this.$router.replace(this.$route.fullPath);
      } catch {
        // L'erreur est conservée dans le store et reste affichée dans l'overlay.
      }
    },
  },
});
</script>

<style scoped>
.runtime-config-overlay {
  align-items: center;
  background:
    radial-gradient(circle at 20% 20%, rgba(var(--suite241-admin-primary-rgb), 0.28), transparent 34%),
    radial-gradient(circle at 80% 78%, rgba(var(--suite241-admin-primary-rgb), 0.46), transparent 38%),
    var(--suite241-admin-nav-bg);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1.5rem;
  position: fixed;
  z-index: 3000;
}

.runtime-config-overlay__panel {
  background: var(--suite241-card-bg);
  border: 1px solid var(--suite241-card-border);
  border-radius: 1.25rem;
  box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.3);
  max-width: 25rem;
  padding: 2rem;
  text-align: center;
  width: min(100%, 25rem);
}

.runtime-config-overlay__logo {
  align-items: center;
  background: var(--suite241-admin-accent-soft);
  border-radius: 1rem;
  display: inline-flex;
  height: 3.25rem;
  justify-content: center;
  margin-bottom: 1rem;
  width: 3.25rem;
}

.runtime-config-overlay__logo img {
  height: 2rem;
  object-fit: contain;
  width: 2rem;
}

.runtime-config-overlay h1 {
  color: var(--suite241-admin-primary);
  font-size: 1.35rem;
  font-weight: 800;
  margin: 0 0 0.5rem;
}

.runtime-config-overlay p {
  color: var(--suite241-field-muted);
  margin: 0;
}

.runtime-config-overlay__gauge {
  background: rgba(var(--suite241-admin-primary-rgb), 0.14);
  border-radius: 999px;
  height: 0.5rem;
  margin-top: 1.5rem;
  overflow: hidden;
}

.runtime-config-overlay__gauge span {
  animation: runtime-config-progress 1.2s ease-in-out infinite;
  background: linear-gradient(
    90deg,
    var(--suite241-admin-primary),
    var(--suite241-admin-accent),
    var(--suite241-admin-primary)
  );
  border-radius: inherit;
  display: block;
  height: 100%;
  width: 48%;
}

.runtime-config-overlay__error {
  color: #b42318 !important;
}

.runtime-config-overlay__retry {
  background: var(--suite241-admin-primary);
  border: 0;
  border-radius: 0.65rem;
  color: #fff;
  font-weight: 700;
  margin-top: 1.5rem;
  padding: 0.7rem 1rem;
}

.runtime-config-overlay__retry:hover,
.runtime-config-overlay__retry:focus-visible {
  background: var(--suite241-admin-primary-hover);
}

@keyframes runtime-config-progress {
  0% { transform: translateX(-110%); }
  100% { transform: translateX(320%); }
}
</style>
